import { useCallback, useEffect, useRef, useState } from 'react';
import { pairInitiate, pairStatus } from '../api/pairing';
import { loginWithCode } from '../api/login';
import { prefs } from '../db/prefs';
import { persistAuth } from '../db/persistAuth';
import { cable } from '../socket/cable';
import { generateQrDataUrl } from '../player/qr';
import { useOnline } from '../helpers/useOnline';
import { useI18n } from '../i18n/I18nContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Diagnostics } from '../components/Diagnostics';
import { FullscreenButton } from '../components/FullscreenButton';
import { applyRotation } from '../player/displayConfig';
import { getLocalIp, getNetworkKind, getPublicIp } from '../helpers/deviceInfo';
import { APP_VERSION } from '../config/env';
import type { StreamConfig } from '../types/stream';

const NOT_AVAILABLE = '—';

interface PairingScreenProps {
  onPaired: () => void;
}

type Mode = 'pair' | 'login';

const POLL_INTERVAL_MS = 10_000;

export function PairingScreen({ onPaired }: PairingScreenProps) {
  const online = useOnline();
  const { t } = useI18n();

  const [mode, setMode] = useState<Mode>('pair');

  // Pairing state.
  const [code, setCode] = useState<string>('');
  const [qr, setQr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Existing-device login state.
  const [codeInput, setCodeInput] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Best-effort device info. The browser can't read the LAN IP/MAC like the
  // native app, so we show the public IP (as seen by our server) and fall back
  // to a WebRTC-discovered local IP when offline.
  const [ip, setIp] = useState<string | null>(null);
  const [showDiag, setShowDiag] = useState(false);
  const [rotation, setRotation] = useState(0);
  const networkKind = getNetworkKind();

  const pairedRef = useRef(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const finishAuth = useCallback(
    async (token: string, uuid: string, deviceId: number, cfg: StreamConfig | null) => {
      if (pairedRef.current) return;
      pairedRef.current = true;

      await persistAuth(token, uuid, deviceId, cfg);

      cable.disconnect();
      if (pollRef.current) clearTimeout(pollRef.current);
      onPaired();
    },
    [onPaired],
  );

  const startPairing = useCallback(async () => {
    setLoading(true);
    setError(null);
    setQr(null);
    setCode('');
    pairedRef.current = false;

    try {
      const data = await pairInitiate();
      setCode(data.pairing_code);
      setLoading(false);

      if (data.qr_code_url) {
        const dataUrl = await generateQrDataUrl(data.qr_code_url);
        setQr(dataUrl);
      }

      const expiry = Date.now() + data.expires_in * 1000;
      cable.connect(data.uuid);

      const poll = async () => {
        if (pairedRef.current) return;
        if (Date.now() > expiry) {
          setError(t('pairing.expired'));
          return;
        }
        try {
          const status = await pairStatus(data.uuid);
          if (status.token) {
            await finishAuth(status.token, data.uuid, status.device_id ?? 0, null);
            return;
          }
        } catch {
          // ignore transient errors, keep polling
        }
        pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      };
      pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    } catch (e) {
      setLoading(false);
      setError((e as Error).message || 'Unable to start pairing.');
    }
  }, [finishAuth, t]);

  useEffect(() => {
    void startPairing();

    const offCable = cable.onMessage((type, payload) => {
      if (type !== 'pairing.complete' || pairedRef.current) return;
      const data = payload['data'] as Record<string, unknown> | undefined;
      if (!data?.['paired']) return;
      const token = data['token'] as string | undefined;
      if (!token) return;
      const device = data['device'] as Record<string, unknown> | undefined;
      const deviceId = (device?.['id'] as number) ?? 0;
      const uuid = (device?.['uuid'] as string) ?? '';
      const cfg = (device?.['config'] as StreamConfig | undefined) ?? null;
      void finishAuth(token, uuid, deviceId, cfg);
    });

    return () => {
      offCable();
      if (pollRef.current) clearTimeout(pollRef.current);
      cable.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void prefs.getRotation().then(setRotation);
  }, []);

  // Rotate the whole pairing UI to preview the chosen physical orientation.
  useEffect(() => {
    if (rootRef.current) applyRotation(rootRef.current, rotation);
  }, [rotation]);

  const cycleRotation = useCallback(() => {
    setRotation((prev) => {
      const next = (prev + 90) % 360;
      void prefs.setRotation(next);
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const publicIp = await getPublicIp();
      if (cancelled) return;
      if (publicIp) {
        setIp(publicIp);
        return;
      }
      const localIp = await getLocalIp();
      if (!cancelled && localIp) setIp(localIp);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const submitLogin = useCallback(async () => {
    const digits = codeInput.trim();
    if (!/^\d{6}$/.test(digits)) {
      setLoginError(t('login.invalid'));
      return;
    }
    setLoginBusy(true);
    setLoginError(null);
    try {
      const res = await loginWithCode(Number(digits));
      await finishAuth(res.token, res.device.uuid, res.device.id, res.device.config ?? null);
    } catch (e) {
      setLoginError((e as Error).message || t('login.error'));
    } finally {
      setLoginBusy(false);
    }
  }, [codeInput, finishAuth, t]);

  return (
    <div className="pairing-root" ref={rootRef}>
      <LanguageSwitcher />

      <FullscreenButton className="pairing-fullscreen" />

      <div className={`pairing-online ${online ? '' : 'off'}`}>
        <span className="dot" />
        {online ? t('common.online') : t('common.offline')}
      </div>

      <div className="pairing-center">
        <img className="pairing-logo" src="/img/logo.png" alt="Vizzly" />

        {mode === 'login' ? (
          <div className="pairing-login">
            <div className="pairing-prompt">{t('login.title')}</div>
            <input
              className="pairing-code-input"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitLogin();
              }}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="------"
              disabled={loginBusy}
              autoFocus
            />
            {loginError && <div className="pairing-error">{loginError}</div>}
            <div className="pairing-actions">
              <button
                className="pairing-retry"
                onClick={() => void submitLogin()}
                disabled={loginBusy}
              >
                {loginBusy ? (
                  <span className="pairing-btn-busy">
                    <span className="pairing-spinner-inline" />
                    {t('login.submitting')}
                  </span>
                ) : (
                  t('login.submit')
                )}
              </button>
              <button
                className="pairing-link"
                onClick={() => {
                  setMode('pair');
                  setLoginError(null);
                  setCodeInput('');
                }}
              >
                {t('login.back')}
              </button>
            </div>
          </div>
        ) : error ? (
          <>
            <div className="pairing-error">{error}</div>
            <button className="pairing-retry" onClick={() => void startPairing()}>
              {t('pairing.retry')}
            </button>
          </>
        ) : loading ? (
          <div className="pairing-spinner" />
        ) : (
          <>
            <div className="pairing-prompt">{t('pairing.prompt')}</div>
            <div className="pairing-code">
              {code.split('').map((d, i) => (
                <span key={i}>{d}</span>
              ))}
            </div>
            <div className="pairing-or">{t('pairing.or')}</div>
            {qr && <img className="pairing-qr" src={qr} alt="Pairing QR" />}
            <button className="pairing-link" onClick={() => setMode('login')}>
              {t('pairing.loginExisting')}
            </button>
          </>
        )}
      </div>

      <div className="pairing-hint">
        {t('pairing.hintLabel')} <b>vizzly.net/kurulum</b>
      </div>

      <div className="pairing-footer">
        <div className="pairing-stat">
          {t('pairing.ip')}: <b>{ip ?? NOT_AVAILABLE}</b>
        </div>
        <div className="pairing-stat">
          {t('pairing.network')}:{' '}
          <b>
            {networkKind
              ? t(`network.${networkKind}`)
              : online
                ? t('pairing.connected')
                : t('pairing.noNetwork')}
          </b>
        </div>
        <div className="pairing-stat">
          {t('pairing.version')}: <b>{APP_VERSION}</b>
        </div>
      </div>

      <div className="pairing-tools">
        <button className="diag-trigger" onClick={cycleRotation}>
          {t('pairing.rotation')}: {rotation}°
        </button>
        <button className="diag-trigger" onClick={() => setShowDiag(true)}>
          {t('diag.open')}
        </button>
      </div>

      {showDiag && <Diagnostics onClose={() => setShowDiag(false)} />}
    </div>
  );
}
